import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface BlogPost {
  id: number;
  title: string;
  description: string;
  category: string;
  image: string;
  readTime: string;
  link: string;
}

const BlogPosts: React.FC = () => {
  const navigate = useNavigate();
  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: "UX Audit of Hair Cubed's Product Page (Mobile First Fold)",
      description: "I identified friction points in the first fold and proposed a redesigned layout to reduce purchase flow friction.",
      category: "UX Audit",
      image: "/images/projects/covers/cover-image-hair-cubed-ux-audit.png",
      readTime: "6 min read",
      link: "/blog/hair-cubed-ux-audit"
    },
  ];

  return (
    <section className="section bg-background-primary pt-0">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Blog Posts
          </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Thoughts, insights & UX Audit.
            </p>
        </motion.div>

        <div className="space-y-8">
          {blogPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group cursor-pointer"
              onClick={() => navigate(post.link)}
            >
              <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col md:flex-row">
                <div className="relative w-full md:w-2/5 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                
                <div className="w-full md:w-3/5 p-8 flex flex-col justify-center">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-primary-color bg-primary-color/10 px-3 py-1.5 rounded-full">
                      {post.category}
                    </span>
                    <span className="text-sm text-text-secondary">
                      {post.readTime}
                    </span>
                  </div>
                  
                  <h4 className="text-2xl font-semibold text-text-primary mb-4 group-hover:text-primary-color transition-colors duration-200">
                    {post.title}
                  </h4>
                  
                  <p className="text-text-secondary text-base mb-6 leading-relaxed">
                    {post.description}
                  </p>
                  
                  <div className="flex items-center text-primary-color text-sm font-medium group-hover:translate-x-1 transition-transform duration-200 mt-auto">
                    <span className="mr-1">Read More</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogPosts;
